import React, { useState, useMemo, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Box, Table, TableContainer, TablePagination }  from '@mui/material';
import { AttachmentItem } from '@kimuno/api-sdk/lib/types/responses/Common';
import { useTranslation } from 'react-i18next';

import AttachmentsTableToolbar from './AttachmentsTableToolbar';
import useResponsivePadding from '../../utils/useResponsivePadding';
import { customSizes, palette } from '../../assets/styles/themes/tokens';
import AttachmentsTableHeader, { AttachmentType } from './AttachmentsTableHeader';
import AttachmentsTableBody from './AttachmentsTableBody';
import { selectAttachments } from '../../reducers/products.selectors';
import AttachmentsBreadcrumbs from './AttachmentsBreadcrumbs';
import useScreenSize from '../../utils/useScreenSize';
import useRouter from '../../utils/useRouter';
import { AttachmentsDownloadRequest } from '@kimuno/api-sdk/lib/types/requests/Product';
import services from '../../services';
import initFileDownload from '../utils/downloadIFrame';
import { notifyBugsnagHandledError } from '../../utils/bugsnagClient';
import { useNonThunkAction } from '../../utils/useAction';
import { addError as addErrorAction } from '../../actions/errors.actions';
import { sleep } from '../../utils/sleep';
import { zipRetryTime } from '../../constants';

const DEFAULT_ROWS_PER_PAGE = 10;
export const ALLOWED_NESTED_LEVEL = 2;

const AttachmentsTable = () => {
  const { t } = useTranslation();
  const { router, match, match: { params: { productId } } } = useRouter();
  const [selected, setSelected] = useState<readonly string[]>([]);
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(DEFAULT_ROWS_PER_PAGE);
  const attachmentsData = useSelector(state => selectAttachments(state, productId));
  const addError = useNonThunkAction(addErrorAction);
  const {  isXsScreen } = useScreenSize();

  const pathSegments = match.location.pathname.split('/').filter(Boolean);

  // Calculate the nested level based on the URL path removing 2 segments: 'attachments' and '{productId}'
  const nestedLevel = pathSegments.length - 2;
  const currentFolderName = decodeURIComponent(pathSegments.at(-1) || '');

  const sortOrder = (a: AttachmentItem, b: AttachmentItem) =>
    a.type === b.type ? 0 : a.type.toLowerCase() === AttachmentType.FOLDER ? -1 : 1;

  const currentAttachments: AttachmentItem[] = useMemo(() => {
    if (!attachmentsData) return [];

    const { attachments, folderStructure } = attachmentsData;

    switch (nestedLevel) {
      case 0:
        return attachments.sort(sortOrder);
      case 1:
        return folderStructure.sort(sortOrder);
      case 2:
        return folderStructure.find(item =>
          item.name === currentFolderName && item.type.toLowerCase() === AttachmentType.FOLDER
        )?.files.sort(sortOrder) || [];
      default:
        return [];
    }
  }, [attachmentsData, nestedLevel, currentFolderName]);

  const currentAttachmentsLength = currentAttachments.length;

  const selectedFilesSize = useMemo(() => {
    return currentAttachments
      .filter(item => selected.includes(item.fileName))
      .reduce((sum, item) => sum + item.fileSize, 0);
  }, [selected, currentAttachments]);

  const onClickSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelected = currentAttachments.map(item => item.fileName);
      setSelected(newSelected);
    } else {
      setSelected([]);
    }
  };

  const onClickItemCheckbox = (fileName: string) => {
    const selectedSet = new Set(selected);
    if (selectedSet.has(fileName)) {
      selectedSet.delete(fileName);
    } else {
      selectedSet.add(fileName);
    }
    setSelected(Array.from(selectedSet));
  };

  const onClickFolder = (folderName: string) => {
    const encodedName = encodeURIComponent(folderName);
    router.push(`${match.location.pathname}/${encodedName}`);
  };

  const onClickItemBody = (name: string, type: string) => {
    if (type.toLowerCase() === AttachmentType.FOLDER && nestedLevel < ALLOWED_NESTED_LEVEL) {
      onClickFolder(name);
    }
  };

  const downloadZip = async (zipID: string): Promise<void> => {
    let isFinished = false;
    while (!isFinished) {
      try {
        const { link, error } = await services.product.getAttachmentZipLink(zipID);

        if (link) {
          initFileDownload(link);
          isFinished = true;
        }

        if (error) {
          notifyBugsnagHandledError(new Error(error));
          isFinished = true;
        }

        await sleep(zipRetryTime);  // Throttle calls to prevent API / s3-zipper overload.
      } catch (err) {
        notifyBugsnagHandledError(err as Error);
        isFinished = true;
      }
    }
  };

  const downloadAttachments = async (attachments: AttachmentsDownloadRequest[]): Promise<void> => {
    try {
      const { link, zipID } = await services.product.handleProductAttachmentsDownload(productId, attachments);

      if (link) {
        initFileDownload(link);
      } else if (zipID) {
        await downloadZip(zipID);
      } else {
        addError('No download link or zip ID returned');
      }
    } catch (error) {
      notifyBugsnagHandledError(error as Error);
      addError('Please try again');
    }
  };

  const getAttachmentsByFileNames = (fileNames: readonly string[]): AttachmentsDownloadRequest[] => {
    const result: AttachmentsDownloadRequest[] = [];
    const fileNameSet = new Set(fileNames);

    for (const item of currentAttachments) {
      if (fileNameSet.has(item.fileName)) {
        result.push({
          contentPath: item.contentPath,
          id: item.id,
          type: item.type,
        });
      }
    }

    return result;
  };

  const onClickItemDownload = async (fileName: string): Promise<void> => {
    const matchedAttachments = getAttachmentsByFileNames([fileName]);

    if (matchedAttachments.length === 0) {
      addError(`No attachment found for file: ${fileName}`);
      return;
    }

    await downloadAttachments(matchedAttachments);
  };

  const onClickDownloadSelected = async () => {
    const matched = getAttachmentsByFileNames(selected);

    if (matched.length === 0) {
      addError('No selected attachments found');
      return;
    }

    await downloadAttachments(matched);
  };

  const onClickChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const onClickChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedRows = useMemo(() => {
    const start = page * rowsPerPage;
    const end = start + rowsPerPage;
    const visibleRows = currentAttachments.slice(start, end);
    const empty = page > 0 ? Math.max(0, rowsPerPage - visibleRows.length) : 0;
    return { visibleRows, empty };
  }, [page, rowsPerPage, currentAttachments]);

  const tablePadding = useResponsivePadding();

  const onClickBackButton = () => {
    const currentPath = match.location.pathname;
    const newPath = currentPath.slice(0, currentPath.lastIndexOf('/'));
    router.push(newPath);
  };

  useEffect(() => {
    setPage(0);
    setSelected([]);
  }, [match.location.pathname]);

  return (
    <Box sx={{
      padding: tablePadding,
      paddingBottom: isXsScreen ? 0 : customSizes.paddingBottomLayoutWithIntercomeButton,
      backgroundColor: palette.background.paperElevation1
    }}>
      {isXsScreen &&
        <Box sx={{ paddingBottom: tablePadding }}>
          <AttachmentsBreadcrumbs />
        </Box>
      }
      <AttachmentsTableToolbar
        numSelected={selected.length}
        selectedFilesSize={selectedFilesSize}
        onClickBackButton={nestedLevel > 0 ? onClickBackButton : undefined}
        subFolderTitle={nestedLevel > 0 ? currentFolderName : undefined}
        onClickDownload={onClickDownloadSelected}
      />
      <TableContainer>
        <Table
          aria-labelledby='tableTitle'
        >
          <AttachmentsTableHeader
            numSelected={selected.length}
            onClickSelectAll={onClickSelectAll}
            rowCount={currentAttachmentsLength}
            attachments={currentAttachments}
          />
          <AttachmentsTableBody
            visibleRows={paginatedRows.visibleRows}
            onClickItemBody={onClickItemBody}
            onClickItemCheckbox={onClickItemCheckbox}
            onClickItemDownload={onClickItemDownload}
            selected={selected}
            emptyRows={paginatedRows.empty}
            nestedLevel={nestedLevel}
          />
        </Table>
      </TableContainer>
      <TablePagination
        data-testid='attachment-table-pagination'
        rowsPerPageOptions={[5, 10, 25]}
        labelRowsPerPage={t('Rows per page:')}
        labelDisplayedRows={
          ({ from, to, count }) =>
            `${from}-${to} ${t('of')} ${count !== -1 ? count : `${t('more than')} ${to}`}`
        }
        component='div'
        count={currentAttachmentsLength}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={onClickChangePage}
        onRowsPerPageChange={onClickChangeRowsPerPage}
        sx={{
          '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
            marginBottom: 0,
          },
          '& .MuiToolbar-root': {
            padding: 0,
          }
        }}
      />
    </Box>
  );
};

export default AttachmentsTable;
